import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useDraftMode } from "../../contexts/DraftModeContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useGlobalManualLocationOrder } from "../../contexts/Staffing/GlobalManualLocationOrderContext";
import { useDayWeek } from "../../contexts/WeekView/useDayWeek";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useErrorSnackbar } from "../../hooks/useErrorSnackbar";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import { useScheduleSaves } from "../../hooks/useScheduleSaves";
import { FONT_SIZE } from "../../theme/tokens";
import { type NurseInShift, NurseInShiftCreationSource } from "../../utils/models";
import { compareValues } from "../../utils/multiColumnSorting";
import { getUniqueLocations } from "../../utils/utilFunctions";
import { EditTimePopover } from "../EditTimePopover";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";

type AddToScheduleDialogProps = {
	nurseShift: NurseInShift;
	date: DateTime;
	onClose: () => void;
	/** Save a past one-off correction as a published shift in departments that normally queue edits. */
	applyImmediately?: boolean;
};

/**
 * Build the scheduled shift from a punched actual shift. The actual is already a fully-formed
 * NurseInShift, so we take it as the base — every shift *detail* (times, job, role, location,
 * notes, …) carries over automatically, and any detail we add in the future does too.
 * We only touch the things that genuinely differ for a schedule entry:
 *  1. actual-only punch metadata, which must NOT leak onto the schedule, is stripped;
 *  2. `breaks` is reset to [] — the actual break *punches* aren't a planned-shift detail, matching
 *     the `prepareDataForAddNurse` default for new scheduled rows;
 *  3. the scheduling identity/state (fresh id + pending/published flags) is (re)set.
 */
const buildScheduledShiftFromActual = (actual: NurseInShift, isPending: boolean): NurseInShift => {
	const {
		missingPunchIn: _missingPunchIn,
		missingPunchOut: _missingPunchOut,
		cost: _cost,
		previousStartTime: _previousStartTime,
		previousEndTime: _previousEndTime,
		previousLocation: _previousLocation,
		previousNotes: _previousNotes,
		externalStartTime: _externalStartTime,
		externalEndTime: _externalEndTime,
		...shiftDetails
	} = actual;

	return {
		...shiftDetails,
		breaks: [],
		id: uuidv4(),
		isPublished: true,
		isPending,
		isPendingToDelete: false,
		isPendingToUpdate: false,
		isPendingToMove: false,
		isPendingToSwap: false,
		isDiscard: false,
	};
};

/**
 * Confirmation dialog for turning a punched-but-unscheduled actual shift into a scheduled shift,
 * mirroring the actual's details. No pickers — just confirm/cancel. The insert flows through the
 * standard `addNurse` pipeline (same route `QuickAddNurseDialog` uses).
 */
export const AddToScheduleDialog: FC<AddToScheduleDialogProps> = ({ nurseShift, date, onClose, applyImmediately }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const showErrorSnackbar = useErrorSnackbar();
	const { chosenDepartment } = useChosenDepartment();
	const { isHospital } = useDepartmentFlags();
	const { addNurseToShift } = useScheduleSaves();
	const { shiftTypeOfTimes } = useDayWeek(date);
	const { isDraftMode } = useDraftMode();
	const { customStringOrderMap } = useGlobalManualLocationOrder();
	const {
		addNurse,
		isLoading: { addNurse: addNurseLoading },
	} = useHandleNurseChange();

	const nurseName = nurseShift.name;

	const locationOptions = useMemo<SystemAutoCompleteOption<string>[]>(() => {
		const names = getUniqueLocations(chosenDepartment.locations, false).map((loc) => loc.location);
		return names.sort((a, b) => compareValues(a, b, customStringOrderMap)).map((loc) => ({ label: loc, value: loc }));
	}, [chosenDepartment.locations, customStringOrderMap]);

	const [selectedLocation, setSelectedLocation] = useState<string | null>(nurseShift.location ?? null);
	const [startTime, setStartTime] = useState<DateTime>(nurseShift.startTime);
	const [endTime, setEndTime] = useState<DateTime>(nurseShift.endTime);

	const performSave = useCallback(async () => {
		// Bucket follows the picked times (the scheduler may have edited them), same as QuickAddNurseDialog.
		const errorContext = { nurseId: nurseShift.nurseId, shiftId: nurseShift.id, date: date.toISODate() };
		let shiftType: string;
		try {
			shiftType = shiftTypeOfTimes({ startTime, endTime });
		} catch (e) {
			showErrorSnackbar(t("actual.addToSchedule.dayNotLoaded"), errorContext, e);
			return;
		}
		const nurseToAdd = buildScheduledShiftFromActual(nurseShift, applyImmediately ? false : isDraftMode ? true : isHospital);
		// Apply the scheduler's edits over the mirrored actual details.
		nurseToAdd.startTime = startTime;
		nurseToAdd.endTime = endTime;
		nurseToAdd.shiftType = shiftType;
		nurseToAdd.location = selectedLocation ?? undefined;

		if (!isDraftMode) {
			// In draft mode addNurse is a no-op (the change is picked up from local state by draft
			// auto-tracking), so persist only when not drafting; the local grid update runs in both cases.
			try {
				await addNurse({
					nurse: nurseToAdd,
					date,
					shiftType,
					location: selectedLocation ?? undefined,
					isActual: false,
					source: NurseInShiftCreationSource.DuplicateActual,
					applyImmediately,
				});
			} catch (e) {
				showErrorSnackbar(t("actual.addToSchedule.error", { nurseName }), errorContext, e);
				return;
			}
		}

		try {
			addNurseToShift(nurseToAdd, date, shiftType, false, isDraftMode);
		} catch (e) {
			showErrorSnackbar(t("actual.addToSchedule.error", { nurseName }), errorContext, e);
			return;
		}

		track("add_to_schedule_from_actual", { nurse_id: nurseShift.nurseId, date: date.toISODate() });

		showSnackbar(t("nurse.added.success", { nurseName, day: startTime.toFormat("ccc"), shiftType: t(shiftType) }), "success");
		onClose();
	}, [
		nurseShift,
		date,
		shiftTypeOfTimes,
		isHospital,
		isDraftMode,
		applyImmediately,
		selectedLocation,
		startTime,
		endTime,
		addNurse,
		addNurseToShift,
		track,
		showSnackbar,
		showErrorSnackbar,
		t,
		nurseName,
		onClose,
	]);

	return (
		<Dialog
			open
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			data-testid="add-to-schedule-dialog"
			slotProps={{ paper: { sx: { borderRadius: 2, height: "auto" } } }}
		>
			<DialogTitle sx={{ pb: 1 }}>
				<Typography sx={(theme) => ({ fontWeight: 600, fontSize: theme.typography.body1.fontSize, color: theme.palette.primary.main })}>
					{t("actual.addToSchedule.title", { nurseName, date: date.toFormat("MMMM d") })}
				</Typography>
			</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px" }}>
				<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, color: "#4D5579" }}>{t("actual.addToSchedule.body")}</Typography>
				{locationOptions.length > 0 && (
					<SystemAutoComplete
						options={locationOptions}
						value={selectedLocation ? { label: selectedLocation, value: selectedLocation } : null}
						onChange={(_, option) => setSelectedLocation(option?.value ?? null)}
						textFieldProps={{ label: t("location"), placeholder: t("select.location"), inputSx: { width: 218 }, reserveHeightForHelperText: false }}
						dataTestId="add-to-schedule-location-input"
					/>
				)}
				<EditTimePopover
					jobId={nurseShift.jobId}
					startTime={startTime}
					endTime={endTime}
					onTimeChange={({ newStartTime, newEndTime }) => {
						setStartTime(newStartTime);
						setEndTime(newEndTime);
					}}
					onClose={() => {}}
					anchorEl={null}
					menuWrapper={false}
					hideActions
					hideFullShifts
					hidePatialShiftLengths
					disableAutoCompetePortal={false}
					sx={{ p: 0 }}
				/>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2, flexDirection: { xs: "column", sm: "row" }, gap: 1, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
				<SystemButton buttonVariant="secondary" onClick={onClose} mobileFullWidthPercentage="100%" data-testid="add-to-schedule-cancel-button">
					{t("cancel")}
				</SystemButton>
				<SystemButton loading={addNurseLoading} onClick={performSave} mobileFullWidthPercentage="100%" data-testid="add-to-schedule-submit-button">
					{t("save")}
				</SystemButton>
			</DialogActions>
		</Dialog>
	);
};
